// contexts/ThemeContext.js
import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme } from '@mui/material';
import { Colors } from '../constants/colors';

const ThemeContext = createContext();

export function useAppTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useAppTheme must be used inside AppThemeProvider');
  return ctx;
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState('light'); // 'light' | 'dark'

  const toggleMode = () => setMode(prev => (prev === 'light' ? 'dark' : 'light'));

  const palette = Colors[mode]; // Colors.light or Colors.dark

  // Build the MUI theme from our Colors constants
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main:  palette.primary.main,
            light: palette.primary.light,
            dark:  palette.primary.dark,
          },
          background: {
            default: palette.background.primary,
            paper:   palette.background.secondary,
          },
          text: {
            primary:   palette.text.primary,
            secondary: palette.text.secondary,
          },
          divider: palette.border,
        },
        typography: {
          fontFamily: ['Inter', 'Roboto', 'Arial', 'sans-serif'].join(','),
        },
        components: {
          MuiToolbar: {
            styleOverrides: {
              root: { minHeight: 70 },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: palette.background.primary,
                color: palette.text.primary,
              },
            },
          },
        },
      }),
    [mode, palette]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, palette, Colors, muiTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}